import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import { CircleChip, NumberInput, noTopGutterSx } from 'src/UIKit';
import { daysOfWeekOptions, recurringPeriodOptions } from 'src/modules/campaigns/helpers';
import { RecurringPeriod, RecurringSettings } from 'src/modules/campaigns/types';

type EditableRecurringSettingsField = Exclude<keyof RecurringSettings, 'startTime' | 'endTime'>;

interface RecurringSettingsFormProps {
  disabled?: boolean;
  errors: Record<string, string>;
  recurringSettings: RecurringSettings;
  onDayToggle: (day: number) => void;
  onPeriodChange: (period: RecurringPeriod) => void;
  onSettingsChange: <K extends EditableRecurringSettingsField>(
    name: K,
    value: RecurringSettings[K],
  ) => void;
}

const getDatePickerValue = (date?: string): Dayjs | null => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate : null;
};

export const RecurringSettingsForm: React.FC<RecurringSettingsFormProps> = ({
  disabled = false,
  errors,
  recurringSettings,
  onDayToggle,
  onPeriodChange,
  onSettingsChange,
}) => {
  const startDate = getDatePickerValue(recurringSettings.dateStart);
  const endDate = getDatePickerValue(recurringSettings.dateEnd);

  const handleStartDateChange = (value: Dayjs | null) => {
    const nextValue = value && value.isValid() ? value : null;

    onSettingsChange('dateStart', nextValue ? nextValue.format('YYYY-MM-DD') : '');
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    const nextValue = value && value.isValid() ? value : null;

    onSettingsChange('dateEnd', nextValue ? nextValue.format('YYYY-MM-DD') : '');
  };

  return (
    <Box mb={2.5}>
      <Typography variant="subtitle2" mb={2.5}>
        Recurring Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              format="MM/DD/YYYY"
              value={startDate}
              onChange={handleStartDateChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors['recurringSettings.dateStart'],
                  helperText: errors['recurringSettings.dateStart'] || ' ',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} sx={noTopGutterSx} />

        <Grid item xs={12} sm={6} md={6} sx={noTopGutterSx}>
          <FormControl fullWidth error={!!errors['recurringSettings.period']}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              MenuProps={{ disableScrollLock: true }}
              value={recurringSettings.period}
              onChange={(e: SelectChangeEvent) =>
                onPeriodChange(e.target.value as RecurringPeriod)
              }
              disabled={disabled}
            >
              {recurringPeriodOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors['recurringSettings.period'] || ' '}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={6} sx={noTopGutterSx}>
          <NumberInput
            label="Interval"
            margin="none"
            min={recurringSettings.period === 'minute' ? 5 : 1}
            withThousandSeparator={false}
            value={recurringSettings.interval}
            onChange={values => {
              if (typeof values.floatValue === 'number') {
                onSettingsChange('interval', values.floatValue);
              }
            }}
            disabled={disabled}
            error={!!errors['recurringSettings.interval']}
            helperText={errors['recurringSettings.interval'] || ' '}
            withoutOnBlurVoid={false}
            allowNegative={false}
            allowZero={false}
          />
        </Grid>

        <Grid item xs={12} sx={noTopGutterSx} />

        <Grid item xs={12} sx={noTopGutterSx}>
          <FormControl fullWidth error={!!errors['recurringSettings.daysOfWeek']}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" width="100%">
              <Typography variant="body2">Days of Week:</Typography>
              {daysOfWeekOptions.map(option => (
                <CircleChip
                  key={option.value}
                  label={option.label}
                  title={option.name}
                  size="small"
                  variant={
                    recurringSettings.daysOfWeek.includes(option.value)
                      ? 'filled'
                      : 'outlined'
                  }
                  color={
                    recurringSettings.daysOfWeek.includes(option.value)
                      ? 'primary'
                      : 'default'
                  }
                  disabled={disabled}
                  onClick={() => onDayToggle(option.value)}
                />
              ))}
            </Box>
            <FormHelperText>{errors['recurringSettings.daysOfWeek'] || ' '}</FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12} sx={noTopGutterSx}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              format="MM/DD/YYYY"
              value={endDate}
              minDate={startDate || undefined}
              referenceDate={startDate || dayjs()}
              onChange={handleEndDateChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors['recurringSettings.dateEnd'],
                  helperText: errors['recurringSettings.dateEnd'] || ' ',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </Box>
  );
};
